import dayjs from 'dayjs';
import orderBy from 'lodash.orderby';
import * as Sentry from '@sentry/vue';
import Vue from 'vue';
import Vuex from 'vuex';

import API from './api';
import bus from './bus';
import environment from '@/environment';
import i18n from './i18n';
import {deepCopyObject, getMainTVSpotFromBundle} from '@/utils';
import {CONTENT_SELECTION_MODES} from '@/mixins/contentSelection.js';

export const SUPPORT_HOTLINE = '+49 521 163 927 - 0';
export const SUPPORT_EMAIL = 'support@myshopradio.de';
export const LANDING_PAGE_IMAGE = '/static/woman.jpg';
export const DEFAULT_SPOTCATEGORY_NAME = 'Aktion';

export const IMAGE_TWO_FACTOR_AUTH_VIA_SMS =
  '/static/twofactorauth/MSR_Web_Icon_SMS.png';
export const IMAGE_TWO_FACTOR_AUTH_VIA_APP =
  '/static/twofactorauth/MSR_Web_Icon_QR.png';

Vue.use(Vuex);

function createInitialCommonState() {
  return {
    // Generic progressbar.
    progress: {
      value: 0,
      indeterminate: false,
      _active: false,
    },

    // Routes / URLS.
    dataPrivacyURL: environment.urls.dataprivacy,
    generalTermsAndConditionsUrl: environment.urls.gtc,
    imprintURL: environment.urls.imprint,
    mediaURL: environment.urls.media,
    preRouteQuestion: '',
    unsetPreRouteQuestionOnNotConfirmed: false,
    productionCMSUrl: environment.urls.prod,
    routeHistory: [],
    troubleshootingUrl: environment.urls.troubleshooting,
    websiteURL: environment.urls.website,

    newBadgeURL: environment.urls.prod + '/static/client/images/Badge_NEU.png',

    // Login.
    currentUser: null,
    loginErrors: [],

    // Sites
    sitesPageIndex: 1,
    sitesSearchText: '',
    sitesSelectedGroupIds: [],
    sitesSelectedTagIds: [],
    sitesShowRadioDecoders: false,
    sitesShowTVDecoders: false,
    sitesPage: {
      next: null,
      previous: null,
      results: {
        sites: [],
      },
      num_pages: 0,
    },

    // Campaigns
    campaignsPageIndex: 1,
    campaignsSearchText: '',
    campaignsPage: {
      next: null,
      previous: null,
      results: {
        campaigns: [],
      },
      num_pages: 0,
    },

    // DecoderDetail, AddContentToDecoder: We cache the currently
    // detail-viewed Decoder so we don't have to always refetch it
    // after route changes where both routes need it.
    decoder: {},
    // addDecoderTo.js
    //
    // This dict is used to store the current selection of Decoders in
    // multiple pages. Warning: Do not use it to find out which Decoders
    // exist, as it is initially empty and only populated with ids of
    // Decoders which selection has been changed.
    decoderIdentToSelected: {},
    includeMenuPlayers: false,

    // User management
    pendingUserInvitations: [],
    managedUsers: [],

    // TVTemplates
    tvtemplatesFilterFavorites: false,
    tvtemplatesFilterLandscape: false,
    tvtemplatesFilterPortrait: false,
    tvtemplatesPage: null,
    tvtemplatesPageIndex: 1,
    tvtemplatesPreviewRenderKey: 0,

    // Content Creator
    contentcreatorBookingRequests: [],
    contentcreatorBookings: [],
    contentcreatorCanDoTestBooking: false,

    // Devices
    devices: [],
    devicesSearchText: '',
    devicesPagination: {
      rowsPerPage: -1,
      sortBy: 'site_sortkey',
      descending: false,
    },

    // Menu(s) etc.
    menus: [],
    menusLastFetchedAt: null,
    lastMenuId: null,
    menuAdditives: [],
    menuCategories: [],
    menuItems: [],
    menuItemsSearchText: '',
    lastSelectedMenuCategory: null,
    lastSelectedMenuTemplateId: null,
    storeMenuExpandMenuItems: false,
    storesWithTVDecodersForMenuCategories: [],
    lastStoreWithMenuVisited: null,

    // Tags
    tags: [],
    tagsSearchText: '',

    // Taggable Decoders
    taggableDecoders: [],
    taggableDecodersSelectedTagIds: [],
    taggableDecodersSearchText: '',
    taggableDecodersPagination: {
      rowsPerPage: -1,
      sortBy: 'site_sortkey',
      descending: false,
    },

    // EntryCreationDialog
    lastInitialDates: {
      start: null,
      stop: null,
    },

    // BrandGroupSelection
    //
    // This lookup dict tracks somewhat redundant information: We
    // already know which brand Groups the User has chosen, but we only
    // get this as a list of ids. This is not great for reactive binding,
    // e.g. to mark a BrandGroupCard as selected, as we'd have to
    // iterate the ids list every time something changes using a
    // function instead of binding to a reactive property. We update
    // this dict as the chosen_brand_group ids list changes for that
    // purpose.
    brandGroupIdToSelected: {},

    // Misc.
    branches: [],
    brandings: [],
    categories: [],
    changelogEntries: [],
    contentCreatorDrawerItemsExpanded: false,
    dashboardAdvertisements: [],
    dashboardInfo: null,
    dashboardVisitorcountInfo: null,
    isUsingWebview: false,
    lastBrandingApplied: null,
    latestSpots: [],
    radioChannels: [],
    revision: null,
    revisionDiffersFromBackend: false,
    selectedTimeframes: [],
    showBetaFeatureMarkers: false,
    showDrawer: false,
    specialTVFormatsAllowedToUpload: [],
    timeframes: [],
    userTVFormatsAvailable: [],
    userTVFormatsChoosable: [],
    userWritableStores: [],

    mediumDialogWidth: 480,
    largeDialogWidth: 640,
    extraLargeDialogWidth: 900,

    // Note: These are dynamically changed by a watcher in App.vue.
    desktopMode: false,
    runtimeEnv: {},

    fullscreenMode: false,

    alerts: [],
    numUnseen: {
      contents: null,
      downloads: null,
      radio_contents: null,
      tickers: null,
      tv_contents: null,
      docmgr: null,
    },

    // rememberScrollPos.js
    lastScrollPositionByRoute: {},

    // ContentSelectionOverlay.vue, AddDecoderToSelectedContents.vue
    contentSelectionOverlayMode: CONTENT_SELECTION_MODES.activate,
    selectedContentsForBatchActivation: [],

    // fetchContents.js
    contents: [],

    contentsPage: 0,
    contentsNumPages: 1,
    contentsCachedForRoutePath: null,
    // Please see this documentation on how to add more filter options:
    // https://github.com/myshopradio/cms/wiki/Implementation-Details#inhaltsliste-filter-hinzuf%C3%BCgen
    contentsQuery: {
      minDate: null,
      maxDate: null,
      onAirOnly: false,
      editableOnly: false,
      publishedOnly: false,
      radio: false,
      searchText: '',
      selectedCategoryIds: [],
      selectedGroupIds: [],
      selectedTagIds: [],
      tickers: false,
      tv: false,
      unpublishedOnly: false,
    },

    contentsForceRefetchOnNextVisit: false,

    // Some values intentionally nullified so they differ initially and
    // we can thus detect that we are visiting the page for the first
    // time.
    lastContentsQuery: {
      minDate: null,
      maxDate: null,
      onAirOnly: null,
      editableOnly: null,
      publishedOnly: null,
      radio: null,
      searchText: null,
      selectedCategoryIds: [],
      selectedGroupIds: [],
      selectedTagIds: [],
      tickers: null,
      tv: null,
      unpublishedOnly: null,
    },
    lastContentsQueryCount: 0,
  };
}

const commonModule = {
  state: createInitialCommonState(),
  getters: {
    formRedirectEnabled(state) {
      return (
        state.currentUser &&
        state.currentUser.clientprofile.firstlogin_form_redirect
      );
    },
    formRedirectNeeded(state) {
      return (
        state.currentUser &&
        !state.currentUser.clientprofile.firstlogin_form_submitted
      );
    },
    dsgvoRedirectEnabled(state) {
      return (
        state.currentUser &&
        state.currentUser.clientprofile.dsgvo_compliance_dialog_redirect
      );
    },
    dsgvoRedirectNeeded(state) {
      return (
        state.currentUser &&
        !state.currentUser.clientprofile.dsgvo_compliance_given_at
      );
    },
    promotedRadioChannels(state) {
      return state.radioChannels.filter(
        (channel) => channel.is_new || channel.is_highlighted
      );
    },
    accessibleGroups(state) {
      if (!state.currentUser) {
        return [];
      }
      return state.currentUser.clientprofile.accessible_groups;
    },
    /**
     * Return only accessible Groups where User can also manage content for.
     *
     * Also order them by name.
     */
    uploadgroups(state, getters) {
      const uploadgroups = getters.accessibleGroups.filter((group) => {
        return group.can_manage_content;
      });
      return orderBy(uploadgroups, 'name');
    },
    /**
     * Return object only when both .start and .stop contain a date.
     */
    lastInitialDates(state) {
      if (
        state.lastInitialDates.start !== null &&
        state.lastInitialDates.stop !== null
      ) {
        return state.lastInitialDates;
      }
      return null;
    },
    hasVisibleAlerts(state) {
      return state.alerts.length > 0;
    },
    /**
     * Alias to do explicit 'Am I logged in?' checks.
     */
    isLoggedIn(state) {
      return state.currentUser !== null;
    },
    /**
     * Whether something is being loaded at the moment.
     */
    busy(state) {
      return state.progress.indeterminate || state.progress.value > 0;
    },
    previousRoute(state) {
      return state.routeHistory[state.routeHistory.length - 2];
    },
    userCanSetTimeFrames(state, getters) {
      return state.currentUser && getters.uploadgroups.length > 0;
    },
    userHasDownloads(state) {
      return state.currentUser && state.currentUser.clientprofile.has_downloads;
    },
    /**
     *  Whether User is being manged by some other User.
     *
     *  We still want to him to see the user-management list, but he
     *  won't be able to invite anyone or modify permissions.
     */
    userIsBeingManaged(state) {
      return (
        state.currentUser && state.currentUser.clientprofile.managed_by !== null
      );
    },
    /**
     *  Whether User has the right to manage other people.
     */
    isUsermanager(state, getters) {
      return (
        state.currentUser && state.currentUser.clientprofile.is_usermanager
      );
    },
    isManagedCoadmin(state) {
      return (
        state.currentUser && state.currentUser.clientprofile.is_managed_coadmin
      );
    },
    isUsermanagerOrManagedCoadmin(state, getters) {
      return getters.isUsermanager || getters.isManagedCoadmin;
    },
    userIsSomeAssociationGroupContentManager(state) {
      return (
        state.currentUser &&
        state.currentUser.clientprofile.is_association_group_contentmanager
      );
    },
    userCanUploadContent(state) {
      return (
        state.currentUser && state.currentUser.clientprofile.can_upload_content
      );
    },
    userManagesContentForAtLeastOneGroup(state, getters) {
      return getters.userCanUploadContent;
    },
    userCanWriteToAtLeastOneStore(state) {
      return (
        state.currentUser &&
        state.currentUser.clientprofile.can_write_to_at_least_one_store
      );
    },
    userCanWriteToAtLeastOneHMP(state) {
      return (
        state.currentUser &&
        state.currentUser.clientprofile.can_write_to_at_least_one_hmp
      );
    },
    userCanCreateTickers(state) {
      return (
        state.currentUser && state.currentUser.clientprofile.can_create_tickers
      );
    },
    userCanSeeUserManagementMenuOption(state) {
      return (
        store.getters.userIsBeingManaged ||
        store.getters.isUsermanagerOrManagedCoadmin
      );
    },
    userCanSeeTvTemplates(state) {
      return (
        state.currentUser &&
        state.currentUser.clientprofile.workspace &&
        state.currentUser.clientprofile.has_tvtemplates
      );
    },
    userCanUseBrandings(state) {
      return state.currentUser && !!state.currentUser.clientprofile.workspace;
    },
    /** Whether User has implicit or explicit permission to see content store statistics. */
    userCanSeeContentStoreStatistics(state) {
      return (
        store.getters.userIsSomeAssociationGroupContentManager ||
        (state.currentUser &&
          state.currentUser.clientprofile.can_see_content_store_statistics)
      );
    },
    userHasContentCreatorBooking(state) {
      return (
        state.currentUser &&
        !!state.currentUser.clientprofile.content_creator_plan_booking
      );
    },
    userCanSetStoreAssociationMemberNumber(state) {
      return (
        state.currentUser &&
        state.currentUser.clientprofile &&
        state.currentUser.clientprofile.can_set_store_association_membernumber
      );
    },
    showContentCreatorLandingPageItem(state) {
      // If we don't have any Booking yet, show the landing page item.
      return (
        state.currentUser &&
        store.getters.userCanSeeTvTemplates &&
        // Association Users cannot book themselves for now, they will only see
        // contentcreator pages when we create a booking internally for them.
        !state.currentUser.clientprofile.workspace
          .contains_association_groups &&
        !state.currentUser.clientprofile.content_creator_plan_booking
      );
    },
    showAllContentCreatorItems(state) {
      return (
        state.currentUser &&
        store.getters.userCanSeeTvTemplates &&
        !!state.currentUser.clientprofile.content_creator_plan_booking
      );
    },
  },
  mutations: {
    TOGGLE_DECODER_SELECTED_BY_IDENT(state, decoderIdent) {
      const newVal = !state.decoderIdentToSelected[decoderIdent];
      const partialUpdate = {};
      partialUpdate[decoderIdent] = newVal;
      store.commit('UPDATE_DECODER_IDENT_TO_SELECTED', partialUpdate);
    },
    /**
     * Set given decoders as either selected or deselected.
     *
     * Example:
     *
     *   opts: {
     *     'decoderIdents': ['tvdecoder_1, 'radiodecoder_1'],
     *     'selected': true,
     *   }
     */
    SET_DECODERS_SELECTED_BY_IDENT(state, opts) {
      const partialUpdate = {};
      for (let decoderIdent of opts.decoderIdents) {
        partialUpdate[decoderIdent] = opts.selected;
      }
      store.commit('UPDATE_DECODER_IDENT_TO_SELECTED', partialUpdate);
    },
    DESELECT_ALL_DECODERS(state) {
      state.decoderIdentToSelected = {};
    },
    UPDATE_DECODER_IDENT_TO_SELECTED(state, partialUpdate) {
      state.decoderIdentToSelected = {
        ...state.decoderIdentToSelected,
        ...partialUpdate,
      };
    },
    SET_INCLUDE_MENU_PLAYERS(state, includeMenuPlayers) {
      state.includeMenuPlayers = includeMenuPlayers;
    },
    SET_SPECIAL_TVFORMATS_ALLOWED_TO_UPLOAD(state, specialTVFormats) {
      state.specialTVFormatsAllowedToUpload = specialTVFormats;
    },
    SET_USER_TVFORMATS_CHOOSABLE(state, tvformats) {
      state.userTVFormatsChoosable = tvformats;
    },
    SET_USER_TVFORMATS_AVAILABLE(state, tvformats) {
      state.userTVFormatsAvailable = tvformats;
    },
    SET_SELECTED_TIMEFRAMES(state, timeframes) {
      state.selectedTimeframes = timeframes;
    },
    SET_RUNTIME_ENV(state, obj) {
      state.runtimeEnv = obj;
    },
    SET_LAST_SCROLL_POSITION_FOR_ROUTE(state, payload) {
      state.lastScrollPositionByRoute[payload.routeName] = payload.scrollPos;
    },
    SET_CATEGORIES(state, categories) {
      state.categories = categories;
    },
    SET_MENUS(state, menus) {
      state.menus = menus;
    },
    SET_MENUS_LAST_FETCHED_AT(state, date) {
      state.menusLastFetchedAt = date;
    },
    SET_MENU_ADDITIVES(state, menuAdditives) {
      state.menuAdditives = menuAdditives;
    },
    SET_MENU_CATEGORIES(state, menuCategories) {
      state.menuCategories = menuCategories;
    },
    SET_MENU_ITEMS(state, menuItems) {
      state.menuItems = menuItems;
    },
    SET_MENU_ITEMS_SEARCH_TEXT(state, searchText) {
      state.menuItemsSearchText = searchText;
    },
    UPDATE_MENU_ITEM_IN_LISTS(state, menuItem) {
      for (let i = 0; i < state.menuItems.length; i++) {
        if (state.menuItems[i].id === menuItem.id) {
          Vue.set(state.menuItems, i, menuItem);
          break;
        }
      }
    },
    SET_LAST_SELECTED_MENU_CATEGORY(state, menuCategory) {
      state.lastSelectedMenuCategory = menuCategory;
    },
    SET_LAST_SELECTED_MENU_TEMPLATE_ID(state, menuTemplateId) {
      state.lastSelectedMenuTemplateId = menuTemplateId;
    },
    SET_STORE_MENU_EXPAND_MENU_ITEMS(state, storeMenuExpandMenuItems) {
      state.storeMenuExpandMenuItems = storeMenuExpandMenuItems;
    },
    SET_STORES_WITH_TV_DECODERS_FOR_MENU_CATEGORIES(state, stores) {
      state.storesWithTVDecodersForMenuCategories = stores;
    },
    SET_LAST_STORE_WITH_MENU_VISITED(state, store) {
      state.lastStoreWithMenuVisited = store;
    },
    SET_LAST_MENU_ID(state, menuId) {
      if (state.lastMenuId !== null && state.lastMenuId != menuId) {
        console.log('[DEBUG] Different Menu chosen, reset outdated state');
        // We have visited a different Menu before and may have some of
        // its data still cached (e.g. the categories list): Reset that.
        store.commit('RESET_MENU_STATE');
      }
      state.lastMenuId = menuId;
    },
    RESET_MENU_STATE(state) {
      const initial = createInitialCommonState();

      state.menuAdditives = initial.menuAdditives;
      state.menuCategories = initial.menuCategories;
      state.menuItems = initial.menuItems;

      state.lastSelectedMenuCategory = initial.lastSelectedMenuCategory;
      state.storeMenuExpandMenuItems = initial.storeMenuExpandMenuItems;
    },
    SET_TIMEFRAMES(state, timeframes) {
      state.timeframes = timeframes;
    },
    SET_TAGS(state, tags) {
      state.tags = tags;
    },
    SET_TAGS_SEARCH_TEXT(state, text) {
      state.tagsSearchText = text;
    },
    UPDATE_TAG_IN_LIST(state, tag) {
      for (let idx = 0; idx < state.tags.length; idx++) {
        if (state.tags[idx].id === tag.id) {
          Vue.set(state.tags, idx, tag);
          break;
        }
      }
    },
    SET_TAGGABLE_DECODERS(state, taggableDecoders) {
      state.taggableDecoders = taggableDecoders;
    },
    SET_TAGGABLE_DECODERS_PAGINATION(state, pagination) {
      state.taggableDecodersPagination = pagination;
    },
    RESET_TAGGABLE_DECODERS_PAGINATION(state) {
      const initial = createInitialCommonState();
      state.taggableDecodersPagination = initial.taggableDecodersPagination;
    },
    SET_TAGGABLE_DECODERS_SELECTED_TAG_IDS(state, ids) {
      state.taggableDecodersSelectedTagIds = ids;
    },
    SET_TAGGABLE_DECODERS_SEARCH_TEXT(state, text) {
      state.taggableDecodersSearchText = text;
    },
    SET_CONTENTS_NUM_PAGES(state, numPages) {
      state.contentsNumPages = numPages;
    },
    SET_CONTENTS_PAGE(state, page) {
      state.contentsPage = page;
    },
    SET_LAST_CONTENTS_QUERY(state, lastContentsQuery) {
      state.lastContentsQuery = lastContentsQuery;
    },
    SET_LAST_CONTENTS_QUERY_COUNT(state, count) {
      state.lastContentsQueryCount = count;
    },
    SET_CONTENTS_QUERY(state, contentsQuery) {
      state.contentsQuery = contentsQuery;
    },
    SET_CONTENTS_QUERY_RADIO(state, radio) {
      state.contentsQuery.radio = radio;
    },
    SET_CONTENTS_QUERY_TV(state, tv) {
      state.contentsQuery.tv = tv;
    },
    SET_CONTENTS_QUERY_TICKERS(state, tickers) {
      state.contentsQuery.tickers = tickers;
    },
    SET_CONTENTS_QUERY_ON_AIR_ONLY(state, onAirOnly) {
      state.contentsQuery.onAirOnly = onAirOnly;
    },
    SET_CONTENTS_QUERY_EDITABLE_ONLY(state, editableOnly) {
      state.contentsQuery.editableOnly = editableOnly;
    },
    SET_CONTENTS_QUERY_UNPUBLISHED_ONLY(state, unpublishedOnly) {
      state.contentsQuery.unpublishedOnly = unpublishedOnly;
    },
    SET_CONTENTS_QUERY_PUBLISHED_ONLY(state, publishedOnly) {
      state.contentsQuery.publishedOnly = publishedOnly;
    },
    SET_CONTENTS_QUERY_MIN_DATE(state, date) {
      state.contentsQuery.minDate = date;
    },
    SET_CONTENTS_QUERY_MAX_DATE(state, date) {
      state.contentsQuery.maxDate = date;
    },
    SET_CONTENTS_QUERY_SEARCH_TEXT(state, searchText) {
      state.contentsQuery.searchText = searchText;
    },
    SET_CONTENTS_QUERY_SELECTED_CATEGORY_IDS(state, selectedCategoryIds) {
      state.contentsQuery.selectedCategoryIds = selectedCategoryIds;
    },
    SET_CONTENTS_QUERY_SELECTED_TAG_IDS(state, selectedTagIds) {
      state.contentsQuery.selectedTagIds = selectedTagIds;
    },
    ADD_CONTENTS_QUERY_SELECTED_GROUP_ID(state, id) {
      state.contentsQuery.selectedGroupIds.push(id);
    },
    REMOVE_CONTENTS_QUERY_SELECTED_GROUP_ID(state, id) {
      const idx = state.contentsQuery.selectedGroupIds.indexOf(id);
      state.contentsQuery.selectedGroupIds.splice(idx, 1);
    },
    SET_CONTENTS_CACHED_FOR_ROUTE_PATH(state, path) {
      state.contentsCachedForRoutePath = path;
    },
    SET_CONTENTS(state, contents) {
      state.contents = contents;
    },
    RESET_CONTENTS(state) {
      state.contents = [];
    },
    SET_CONTENT_SELECTION_OVERLAY_MODE(state, mode) {
      state.contentSelectionOverlayMode = mode;
    },
    SET_SELECTED_CONTENTS_FOR_BATCH_ACTIVATION(
      state,
      selectedContentsForBatchActivation
    ) {
      state.selectedContentsForBatchActivation =
        selectedContentsForBatchActivation;
    },
    RESET_SELECTED_CONTENTS_FOR_BATCH_ACTIVATION(state) {
      const initial = createInitialCommonState();
      state.selectedContentsForBatchActivation =
        initial.selectedContentsForBatchActivation;
    },
    SET_FORCE_REFETCH_CONTENTS_ON_NEXT_VISIT(
      state,
      contentsForceRefetchOnNextVisit
    ) {
      state.contentsForceRefetchOnNextVisit = contentsForceRefetchOnNextVisit;
    },
    SET_SHOW_DRAWER(state, flag) {
      state.showDrawer = flag;
    },
    SET_SHOW_BETA_FEATURE_MARKERS(state, flag) {
      state.showBetaFeatureMarkers = flag;
    },
    SET_DESKTOP_MODE(state, flag) {
      state.desktopMode = flag;
    },
    SET_FULLSCREEN_MODE(state, flag) {
      state.fullscreenMode = flag;
    },
    SET_LAST_INITIAL_DATES(state, dates) {
      state.lastInitialDates.start = dates.start;
      state.lastInitialDates.stop = dates.stop;
    },
    RESET_SITES_FILTERS(state) {
      const initial = createInitialCommonState();
      state.sitesSearchText = initial.sitesSearchText;
      state.sitesShowRadioDecoders = initial.sitesShowRadioDecoders;
      state.sitesShowTVDecoders = initial.sitesShowTVDecoders;
      state.sitesSelectedGroupIds = initial.sitesSelectedGroupIds;
      state.sitesSelectedTagIds = initial.sitesSelectedTagIds;
    },
    RESET_CONTENT_FILTERS(state) {
      const initial = createInitialCommonState();
      state.contentsPage = initial.contentsPage;
      state.contentsNumPages = initial.contentsNumPages;
      state.contentsCachedForRoutePath = initial.contentsCachedForRoutePath;
      state.contentsQuery = initial.contentsQuery;
    },
    RESET_LAST_CONTENTS_QUERY(state) {
      const initial = createInitialCommonState();
      state.lastContentsQuery = initial.lastContentsQuery;
    },
    INVALIDATE_CONTENTS_CACHE(state) {
      store.commit('RESET_CONTENTS');
      // Resetting all filters e.g. for the new route makes sense,
      // because there is likely more confusion then benefit to
      // reusing things like searchText or onAirOnly here.
      store.commit('RESET_CONTENT_FILTERS');
      store.commit('RESET_LAST_CONTENTS_QUERY');
    },
    /**
     * Make sure changed object is replaced in the cached content list(s).
     *
     * This means if we are currently filtering the content list by some
     * content attribute like 'published', and we change that state on
     * some content object, this needs to be reflected in that list,
     * otherwise navigating backwards may show invalid results.
     *
     * These lists are currently: contents, latestSpots
     *
     * Comparison is based on id and kind, so this works for RadioSpot,
     * TVSpot, TickerMessage and UploadImage objects. If a filter on
     * the list excludes the changed content, make sure it is removed
     * from that list.
     *
     * If the object is not in there yet, do nothing.
     */
    UPDATE_CONTENT_IN_LISTS(state, content) {
      const contentLists = [state.contents, state.latestSpots];

      contentLists.forEach((contentList) => {
        for (var i = contentList.length - 1; i >= 0; i--) {
          const contentItem = contentList[i];
          if (
            contentItem.kind === content.kind &&
            contentItem.id === content.id
          ) {
            // On-Air
            let onAirOk = true;
            if (
              state.contentsQuery.onAirOnly &&
              !(content.is_on_air || content.is_planned_to_air)
            ) {
              onAirOk = false;
            }

            // Published / Unpublished
            let publishOk = true;
            if (
              state.contentsQuery.unpublishedOnly &&
              state.contentsQuery.publishedOnly
            ) {
              // They cancel each other, we are back at the default.
            } else {
              if (state.contentsQuery.unpublishedOnly && content.published) {
                publishOk = false;
              }
              if (state.contentsQuery.publishedOnly && !content.published) {
                publishOk = false;
              }
            }

            const shouldRemove = !onAirOk || !publishOk;
            if (shouldRemove) {
              contentList.splice(i, 1);
            } else {
              // Should replace instead.
              contentList.splice(i, 1, content);
            }
            break;
          }
        }
      });
    },
    /**
     * Same as UPDATE_CONTENT_IN_LISTS, but removing instead.
     */
    REMOVE_CONTENT_IN_LISTS(state, content) {
      const contentLists = [state.contents, state.latestSpots];

      contentLists.forEach((contentList) => {
        for (var i = contentList.length - 1; i >= 0; i--) {
          const contentItem = contentList[i];
          if (
            contentItem.kind === content.kind &&
            contentItem.id === content.id
          ) {
            // https://vuejs.org/v2/guide/list.html#Caveats
            contentList.splice(i, 1);
            break;
          }
        }
      });
    },
    SET_NUM_UNSEEN(state, data) {
      state.numUnseen = data;
    },
    SET_LOCALE(state, locale) {
      i18n.locale = locale;
      // Note: dayjs locales are pre-loaded in main.js to be included in the build.
      dayjs.locale(locale);
      localStorage.setItem('locale', locale);
    },
    REMOVE_ALERT_BY_ID(state, alertId) {
      for (var i = state.alerts.length - 1; i >= 0; i--) {
        var alert = state.alerts[i];
        if (alert.id === alertId) {
          state.alerts.splice(i, 1);
          break;
        }
      }
    },
    ADD_ALERT(state, alert) {
      state.alerts.push(alert);
    },
    SET_LATEST_SPOTS(state, spots) {
      state.latestSpots = spots;
    },
    SET_CHANGELOG_ENTRIES(state, changelogEntries) {
      state.changelogEntries = changelogEntries;
    },
    SET_DASHBOARD_ADVERTISEMENTS(state, advertisements) {
      state.dashboardAdvertisements = advertisements;
    },
    SET_DASHBOARD_INFO(state, info) {
      state.dashboardInfo = info;
    },
    SET_DASHBOARD_VISITORCOUNT_INFO(state, info) {
      state.dashboardVisitorcountInfo = info;
    },
    SET_PRE_ROUTE_QUESTION(state, question) {
      state.preRouteQuestion = question;
    },
    SET_UNSET_PRE_ROUTE_QUESTION_ON_NOT_CONFIRMED(state, flag) {
      state.unsetPreRouteQuestionOnNotConfirmed = flag;
    },
    UNSET_PRE_ROUTE_QUESTION(state) {
      state.preRouteQuestion = '';
    },
    APPEND_ROUTE_TO_HISTORY(state, route) {
      state.routeHistory.push(route);
    },
    /**
     * Clear the current user/auth and all vuex data.
     *
     * Note: Keep in mind this also resets things like drawer state.
     */
    RESET_ENTIRE_STATE(state) {
      this.commit('UNSET_CURRENT_USER');

      const initial = createInitialCommonState();
      Object.keys(initial).forEach((key) => {
        // Skip this one, we want to remember it.
        if (key === 'desktopMode') {
          return;
        }
        state[key] = initial[key];
      });
    },
    SET_PROGRESS_VALUE(state, value) {
      state.progress.value = value;

      if (!value && !state.progress.indeterminate) {
        state.progress._active = false;
      } else {
        state.progress._active = true;
      }
    },
    SET_PROGRESS_INDETERMINATE(state, indeterminate) {
      state.progress.indeterminate = indeterminate;
      if (!indeterminate && !state.progress.value) {
        state.progress._active = false;
      } else {
        state.progress._active = true;
      }
    },
    SET_CURRENT_USER(state, user) {
      state.currentUser = user;

      if (!user) {
        return;
      }

      // Populate initial Tags list from profile payload.
      state.tags = user.clientprofile.tags;

      // Apple's app policies don't like the word 'BETA', as they consider
      // it showing incomplete stuff and as such our iOS app updates may
      // get rejected when they see it e.g. on the dashboard, so hide it
      // for the designated app review User we give them.
      store.commit(
        'SET_SHOW_BETA_FEATURE_MARKERS',
        user &&
          user.clientprofile &&
          user.clientprofile.can_see_beta_feature_markers
      );

      // Allow Sentry to associate errors with some user specifics.
      Sentry.setUser({
        email: user.email,
        first_name: user.first_name,
        id: user.id,
        last_name: user.last_name,
        username: user.username,
      });
    },
    UPDATE_CURRENT_USER_PROFILE_TAGS(state, tags) {
      if (!state.currentUser) {
        return;
      }
      state.currentUser.clientprofile.tags = tags;
    },
    /**
     * This is basically the client-side 'logout' right now.
     *
     * It removes the currentUser object and the authorization token.
     */
    UNSET_CURRENT_USER(state) {
      // Removing the user is pretty much a logout and we want to
      // hide the drawer in this case, otherwise UI may look wrong.
      store.commit('SET_SHOW_DRAWER', false);

      store.commit('SET_CURRENT_USER', null);
      API.deleteCookie('authtoken');

      // https://docs.sentry.io/platforms/javascript/enriching-events/identify-user/
      Sentry.configureScope((scope) => scope.setUser(null));
    },
    SET_LOGIN_ERRORS(state, errors) {
      state.loginErrors = errors;
    },
    SET_USER_WRITABLE_STORES(state, stores) {
      state.userWritableStores = stores;
    },
    SET_STORE_ASSOCIATION_MEMBERNUMBER_IN_SITES_LIST(state, data) {
      const storeId = data.storeId;
      const associationMemberNumber = data.associationMemberNumber;

      for (const site of state.sitesPage.results.sites) {
        if (site.store.id === storeId) {
          site.store.association_membernumber = associationMemberNumber;
          break;
        }
      }
    },
    SET_USER_CHOSEN_BRAND_GROUPS(state, ids) {
      state.currentUser.clientprofile.chosen_brand_groups = ids;

      // Update lookup dict that we use for displaying selection state
      // in a reactive way, without having to loop the ids list.
      state.brandGroupIdToSelected = {};
      for (let id of ids) {
        state.brandGroupIdToSelected[id] = true;
      }
    },
    SET_RADIO_CHANNELS(state, radioChannels) {
      state.radioChannels = radioChannels;
    },
    SET_BRANCHES(state, branches) {
      state.branches = branches;
    },
    SET_BRANDINGS(state, brandings) {
      state.brandings = brandings;

      // Update lastBrandingApplied, which is a copy of a certain
      // Branding, if we have a matching Branding in the new list.
      if (state.lastBrandingApplied) {
        const matchingBranding = state.brandings.filter(
          (branding) => branding.id == state.lastBrandingApplied.id
        )[0];
        if (matchingBranding) {
          store.commit('SET_LAST_BRANDING_APPLIED', matchingBranding);
        }
      }
    },
    SET_LAST_BRANDING_APPLIED(state, branding) {
      state.lastBrandingApplied = deepCopyObject(branding);
    },
    RESET_CAMPAIGNS_PAGE_INDEX(state) {
      const initial = createInitialCommonState();
      state.campaignsPageIndex = initial.campaignsPageIndex;
    },
    SET_CAMPAIGNS_PAGE_INDEX(state, index) {
      state.campaignsPageIndex = index;
    },
    SET_CAMPAIGNS_PAGE(state, page) {
      state.campaignsPage = page;
    },
    SET_CAMPAIGNS_SEARCH_TEXT(state, text) {
      state.campaignsSearchText = text;
    },
    SET_TV_TEMPLATES_PAGE(state, tvtemplatesPage) {
      state.tvtemplatesPage = tvtemplatesPage;
    },
    SET_TV_TEMPLATES_PAGE_INDEX(state, pageIndex) {
      state.tvtemplatesPageIndex = pageIndex;
    },
    SET_TV_TEMPLATES_FILTER_LANDSCAPE(state, flag) {
      state.tvtemplatesFilterLandscape = flag;
    },
    SET_TV_TEMPLATES_FILTER_PORTRAIT(state, flag) {
      state.tvtemplatesFilterPortrait = flag;
    },
    SET_TV_TEMPLATES_FILTER_FAVORITES(state, flag) {
      state.tvtemplatesFilterFavorites = flag;
    },
    RESET_TV_TEMPLATES_FILTER(state) {
      const initial = createInitialCommonState();
      state.tvtemplatesFilterFavorites = initial.tvtemplatesFilterFavorites;
      state.tvtemplatesFilterLandscape = initial.tvtemplatesFilterLandscape;
      state.tvtemplatesFilterPortrait = initial.tvtemplatesFilterPortrait;
      state.tvtemplatesPageIndex = initial.tvtemplatesPageIndex;
      state.tvtemplatesSearchText = initial.tvtemplatesSearchText;
    },
    INCREMENT_TV_TEMPLATES_PREVIEW_RENDERKEY(state) {
      state.tvtemplatesPreviewRenderKey += 1;
    },
    SET_DEVICES(state, devices) {
      state.devices = devices;
    },
    SET_DEVICES_SEARCH_TEXT(state, text) {
      state.devicesSearchText = text;
    },
    SET_DEVICES_PAGINATION(state, pagination) {
      state.devicesPagination = pagination;
    },
    RESET_DEVICES_PAGINATION(state) {
      const initial = createInitialCommonState();
      state.devicesPagination = initial.devicesPagination;
    },
    SET_SITES_PAGE_INDEX(state, pageIndex) {
      state.sitesPageIndex = pageIndex;
    },
    SET_SITES_SEARCH_TEXT(state, text) {
      state.sitesSearchText = text;
    },
    SET_SITES_SHOW_RADIO_DECODERS(state, flag) {
      state.sitesShowRadioDecoders = flag;
    },
    SET_SITES_SHOW_TV_DECODERS(state, flag) {
      state.sitesShowTVDecoders = flag;
    },
    SET_CONTENTCREATOR_CAN_DO_TEST_BOOKING(state, flag) {
      state.contentcreatorCanDoTestBooking = flag;
    },
    SET_CONTENTCREATOR_BOOKING_REQUESTS(state, bookingRequests) {
      state.contentcreatorBookingRequests = bookingRequests;
    },
    SET_CONTENTCREATOR_BOOKINGS(state, bookings) {
      state.contentcreatorBookings = bookings;
    },
    ADD_SITES_SELECTED_GROUP_ID(state, id) {
      state.sitesSelectedGroupIds.push(id);
    },
    REMOVE_SITES_SELECTED_GROUP_ID(state, id) {
      const idx = state.sitesSelectedGroupIds.indexOf(id);
      state.sitesSelectedGroupIds.splice(idx, 1);
    },
    SET_SITES_SELECTED_TAG_IDS(state, ids) {
      state.sitesSelectedTagIds = ids;
    },
    /**
     * When a Tag has been modified, update occurrences across the app.
     */
    UPDATE_SITES_DECODER_TAGS(state, tags) {
      if (
        !tags.length ||
        !state.sitesPage ||
        !state.sitesPage.results ||
        !state.sitesPage.results.sites
      ) {
        return;
      }
      const tagIds = tags.map((tag) => tag.id);
      const tagIdToTag = {};
      for (const tag of tags) {
        tagIdToTag[tag.id] = tag;
      }

      for (const site of state.sitesPage.results.sites) {
        for (const decoderListName of ['radiodecoders', 'tvdecoders']) {
          for (const decoder of site[decoderListName]) {
            for (let i = 0; i < decoder.tags.length; i++) {
              const tag = decoder.tags[i];
              if (tag && tagIds.indexOf(tag.id) !== -1) {
                decoder.tags[i] = tagIdToTag[tag.id]; // Update in-place.
              }
            }
          }
        }
      }
    },
    /**
     * Set current sites page.
     */
    SET_SITES_PAGE(state, page) {
      state.sitesPage = page;
    },
    SET_SITE_EXPANDED(state, args) {
      args.site.expanded = args.expanded;
    },
    RESET_SITES(state) {
      const initial = createInitialCommonState();
      state.sitesPageIndex = initial.sitesPageIndex;
      state.sitesPage = initial.sitesPage;
    },
    SET_IS_USING_WEBVIEW(state, isUsingWebview) {
      state.isUsingWebview = isUsingWebview;
    },
    SET_DECODER(state, decoder) {
      state.decoder = decoder;
    },
    SET_PENDING_USER_INVITATIONS(state, invitations) {
      state.pendingUserInvitations = invitations;
    },
    SET_MANAGED_USERS(state, managedUsers) {
      state.managedUsers = managedUsers;
    },
    REMOVE_MANAGED_USER(state, managedUser) {
      const idx = state.managedUsers.map((u) => u.id).indexOf(managedUser.id);
      state.managedUsers.splice(idx, 1);
    },
    /**
     * Update first_name and last_name of a managed User object.
     *
     * opts.id (Number)
     * opts.firstName (String)
     * opts.lastName (String)
     */
    EDIT_MANAGED_USER_NAME(state, opts) {
      const idx = state.managedUsers.map((u) => u.id).indexOf(opts.id);
      const managedUser = state.managedUsers[idx];
      managedUser.first_name = opts.firstName;
      managedUser.last_name = opts.lastName;
    },
    /**
     * Add new GroupAccess to a managed User's groupaccesses list.
     *
     * opts.managedUser (Object)
     * opts.groupaccess (Object)
     */
    ADD_GROUPACCESS_TO_MANAGED_USER(state, opts) {
      const managedUser = opts.user;
      const groupaccess = opts.groupaccess;

      const idx = state.managedUsers.map((u) => u.id).indexOf(managedUser.id);
      state.managedUsers[idx].groupaccesses.push(groupaccess);
    },
    /**
     * Add new StoreAccess to a managed User's storeaccesses list.
     *
     * opts.managedUser (Object)
     * opts.storeaccess (Object)
     */
    ADD_STOREACCESS_TO_MANAGED_USER(state, opts) {
      const managedUser = opts.user;
      const storeaccess = opts.storeaccess;

      const idx = state.managedUsers.map((u) => u.id).indexOf(managedUser.id);
      state.managedUsers[idx].storeaccesses.push(storeaccess);
    },
    /**
     * Update existing GroupAccess from a managed User by replacement.
     *
     * opts.managedUser (Object)
     * opts.groupaccess (Object)
     */
    UPDATE_GROUPACCESS_OF_MANAGED_USER(state, opts) {
      const managedUser = opts.user;
      const groupaccess = opts.groupaccess;

      const userIndex = state.managedUsers
        .map((u) => u.id)
        .indexOf(managedUser.id);
      const user = state.managedUsers[userIndex];
      const accessIndex = user.groupaccesses
        .map((a) => a.id)
        .indexOf(groupaccess.id);
      Vue.set(
        state.managedUsers[userIndex].groupaccesses,
        accessIndex,
        groupaccess
      );
    },
    /**
     * Update existing StoreAccess from a managed User by replacement.
     *
     * opts.managedUser (Object)
     * opts.storeaccess (Object)
     */
    UPDATE_STOREACCESS_OF_MANAGED_USER(state, opts) {
      const managedUser = opts.user;
      const storeaccess = opts.storeaccess;

      const userIndex = state.managedUsers
        .map((u) => u.id)
        .indexOf(managedUser.id);
      const user = state.managedUsers[userIndex];
      const accessIndex = user.storeaccesses
        .map((a) => a.id)
        .indexOf(storeaccess.id);
      Vue.set(
        state.managedUsers[userIndex].storeaccesses,
        accessIndex,
        storeaccess
      );
    },
    /**
     * Remove existing GroupAccess from a managed User by ID.
     *
     * opts.managedUser (Object)
     * opts.groupaccessId (Number)
     */
    REMOVE_GROUPACCESS_FROM_MANAGED_USER(state, opts) {
      const managedUser = opts.user;
      const groupaccessId = opts.groupaccessId;

      const userIndex = state.managedUsers
        .map((u) => u.id)
        .indexOf(managedUser.id);
      const user = state.managedUsers[userIndex];
      const accessIndex = user.groupaccesses
        .map((a) => a.id)
        .indexOf(groupaccessId);
      state.managedUsers[userIndex].groupaccesses.splice(accessIndex, 1);
    },
    /**
     * Remove existing StoreAccess from a managed User by ID.
     *
     * opts.managedUser (Object)
     * opts.storeaccessId (Number)
     */
    REMOVE_STOREACCESS_FROM_MANAGED_USER(state, opts) {
      const managedUser = opts.user;
      const storeaccessId = opts.storeaccessId;

      const userIndex = state.managedUsers
        .map((u) => u.id)
        .indexOf(managedUser.id);
      const user = state.managedUsers[userIndex];
      const accessIndex = user.storeaccesses
        .map((a) => a.id)
        .indexOf(storeaccessId);
      state.managedUsers[userIndex].storeaccesses.splice(accessIndex, 1);
    },
    RESET_DECODER(state, decoder) {
      state.decoder = createInitialCommonState().decoder;
    },
    SET_REVISION(state, commitHash) {
      state.revision = commitHash;
    },
    SET_REVISION_DIFFERS_FROM_BACKEND(state, isDifferent) {
      state.revisionDiffersFromBackend = isDifferent;
    },
    SET_CONTENTCREATOR_DRAWER_ITEMS_EXPANDED(state, expanded) {
      state.contentCreatorDrawerItemsExpanded = expanded;
    },
  },
  actions: {
    RETRIEVE_REVISION(context) {
      return API.retrieveGitCommitHash().then((response) => {
        context.commit('SET_REVISION', response.data);
      });
    },
    RETRIEVE_REVISION_DIFFERS_FROM_BACKEND(context) {
      const currentRevision = context.state.revision;
      if (currentRevision === null) {
        context.commit('SET_REVISION_DIFFERS_FROM_BACKEND', false);
        return;
      }
      return API.retrieveGitCommitHash().then((response) => {
        const isDifferent = currentRevision !== response.data;
        context.commit('SET_REVISION_DIFFERS_FROM_BACKEND', isDifferent);
      });
    },
    /**
     * Fetch Decoder specified via opts.
     *
     * opts.ctype (String): 'tv' or 'radio'
     * opts.decoderId (Number)
     */
    RETRIEVE_DECODER(context, opts) {
      return API.retrieveDecoder(opts.ctype, opts.decoderId).then(
        (response) => {
          if (window.debug) {
            // Pretty-print the serialized Decoder JSON.
            console.log(JSON.stringify(response.data, null, 2));
          }
          context.commit('SET_DECODER', response.data);
        }
      );
    },
    CHOOSE_SINGLE_BRAND_GROUP(context, group) {
      const chosenBrandGroupIds =
        context.state.currentUser.clientprofile.chosen_brand_groups.slice();
      if (chosenBrandGroupIds.indexOf(group.id) !== -1) {
        return new Promise.resolve();
      }

      chosenBrandGroupIds.push(group.id);
      context.commit('SET_USER_CHOSEN_BRAND_GROUPS', chosenBrandGroupIds);

      // Invalidate contents list as a side effect.
      context.commit('RESET_LAST_CONTENTS_QUERY');

      return API.updateChosenBrandGroups(chosenBrandGroupIds);
    },
    RETRIEVE_IS_USING_WEBVIEW(context) {
      return API.getIsUsingWebview().then((response) => {
        context.commit('SET_IS_USING_WEBVIEW', response.data);
      });
    },
    /**
     * Find out if there are 'unseen' contents/downloads and store results.
     */
    RETRIEVE_NUM_UNSEEN(context) {
      return API.retrieveNumUnseenContents().then((response) => {
        context.commit('SET_NUM_UNSEEN', response.data);
      });
    },
    RETRIEVE_CURRENT_USER(context) {
      return API.whoAmI().then((response) => {
        const user = response.data;
        context.commit('SET_CURRENT_USER', user);
      });
    },
    /**
     * Fetch user only if it has not been done before.
     */
    MAYBE_RETRIEVE_CURRENT_USER(context) {
      if (context.state.currentUser) {
        return Promise.resolve();
      }
      return API.whoAmI().then((response) => {
        const user = response.data;
        context.commit('SET_CURRENT_USER', user);
      });
    },
    /**
     * List /sites and store results.
     *
     * Competing requests that already run will be cancelled.
     */
    LIST_SITES(context, page) {
      const opts = {
        page: page,
        searchText: context.state.sitesSearchText,
        groupIds: context.state.sitesSelectedGroupIds,
        tagIds: context.state.sitesSelectedTagIds,
      };
      if (context.state.sitesShowRadioDecoders) {
        opts.radio = true;
      }
      if (context.state.sitesShowTVDecoders) {
        opts.tv = true;
      }

      API.cancelCurrentRequest();

      return API.listSites(opts).then((response) => {
        const page = response.data;
        context.commit('SET_SITES_PAGE', page);
      });
    },
    /**
     * List /channels and store results.
     */
    LIST_RADIO_CHANNELS(context) {
      return API.listRadioChannels().then((response) => {
        context.commit('SET_RADIO_CHANNELS', response.data);
      });
    },
    LIST_BRANCHES(context) {
      return API.listBranches().then((response) => {
        context.commit('SET_BRANCHES', response.data);
      });
    },
    LIST_BRANDINGS(context) {
      return API.listBrandings().then((response) => {
        context.commit('SET_BRANDINGS', response.data);
      });
    },
    MAYBE_LIST_BRANDINGS(context) {
      if (context.state.brandings.length > 0) {
        return Promise.resolve();
      }
      return context.dispatch('LIST_BRANDINGS');
    },
    RETRIEVE_CONTENTCREATOR_CAN_DO_TEST_BOOKING(context) {
      return API.retrieveContentCreatorCanDoTestBooking().then((response) => {
        context.commit('SET_CONTENTCREATOR_CAN_DO_TEST_BOOKING', response.data);
      });
    },
    LIST_CONTENTCREATOR_BOOKING_REQUESTS(context) {
      return API.listContentCreatorBookingRequests().then((response) => {
        const bookingRequests = response.data;
        context.commit('SET_CONTENTCREATOR_BOOKING_REQUESTS', bookingRequests);
      });
    },
    LIST_CONTENTCREATOR_BOOKINGS(context) {
      return API.listContentCreatorPlanBookings().then((response) => {
        const bookings = response.data;
        context.commit('SET_CONTENTCREATOR_BOOKINGS', bookings);
      });
    },
    /**
     * List /campaigns and store results.
     */
    LIST_CAMPAIGNS(context) {
      const opts = {
        pageIndex: context.state.campaignsPageIndex || 1,
        searchText: context.state.campaignsSearchText || '',
      };
      return API.listCampaignsForUser(opts).then((response) => {
        context.commit('SET_CAMPAIGNS_PAGE', response.data);
      });
    },
    MAYBE_LIST_CAMPAIGNS(context) {
      if (context.state.campaignsPage.results.length > 0) {
        return Promise.resolve();
      }
      return context.dispatch('LIST_CAMPAIGNS');
    },
    LIST_TV_TEMPLATES(context) {
      const opts = {
        pageIndex: context.state.tvtemplatesPageIndex,
      };
      const branchId = context.state.lastBrandingApplied
        ? context.state.lastBrandingApplied.branch_id
        : null;
      if (branchId) {
        opts.branchId = branchId;
      }
      if (context.state.tvtemplatesFilterLandscape) {
        opts.landscape = true;
      }
      if (context.state.tvtemplatesFilterPortrait) {
        opts.portrait = true;
      }
      if (context.state.tvtemplatesFilterFavorites) {
        opts.favorites = true;
      }

      return API.listTVTemplatesForUser(opts).then((response) => {
        const page = response.data;
        context.commit('SET_TV_TEMPLATES_PAGE', page);
      });
    },
    LIST_DEVICES(context) {
      return API.listDevices().then((response) => {
        context.commit('SET_DEVICES', response.data);
      });
    },
    /**
     * List /stores/writable and store results.
     */
    LIST_USER_WRITABLE_STORES(context) {
      return API.listUserWritableStores().then((response) => {
        context.commit('SET_USER_WRITABLE_STORES', response.data);
      });
    },
    LIST_CATEGORIES(context) {
      return API.listUserSpotCategories().then((response) => {
        context.commit('SET_CATEGORIES', response.data);
      });
    },
    MAYBE_LIST_CATEGORIES(context) {
      if (context.state.categories.length > 0) {
        return Promise.resolve();
      }
      return context.dispatch('LIST_CATEGORIES');
    },
    LIST_TIMEFRAMES(context) {
      return API.listTimeframes().then((response) => {
        context.commit('SET_TIMEFRAMES', response.data);
      });
    },
    LIST_MENUS(context) {
      return API.listMenus().then((response) => {
        const menus = response.data;
        context.commit('SET_MENUS', menus);
        context.commit('SET_MENUS_LAST_FETCHED_AT', new Date());
      });
    },
    LIST_MENU_ADDITIVES(context, menuId) {
      return API.listMenuAdditives(menuId).then((response) => {
        const menuAdditives = response.data;
        context.commit('SET_MENU_ADDITIVES', menuAdditives);
      });
    },
    LIST_MENU_CATEGORIES(context, menuId) {
      return API.listMenuCategories(menuId).then((response) => {
        const menuCategories = response.data;
        context.commit('SET_MENU_CATEGORIES', menuCategories);
      });
    },
    LIST_MENU_ITEMS(context, menuId) {
      return API.listMenuItems(menuId).then((response) => {
        const menuItems = response.data;
        context.commit('SET_MENU_ITEMS', menuItems);
      });
    },
    RETRIEVE_MENU_ITEM(context, menuItemId) {
      return API.retrieveMenuItem(menuItemId).then((response) => {
        const menuItem = response.data;
        context.commit('UPDATE_MENU_ITEM_IN_LISTS', menuItem);
      });
    },
    LIST_STORES_WITH_TV_DECODERS_FOR_MENU_CATEGORIES(context, menuId) {
      return API.listStoresWithTVDecodersForMenuCategories(menuId).then(
        (response) => {
          const stores = response.data;
          context.commit(
            'SET_STORES_WITH_TV_DECODERS_FOR_MENU_CATEGORIES',
            stores
          );
        }
      );
    },
    LIST_TAGS(context) {
      return API.listTags().then((response) => {
        const tags = response.data;
        context.commit('SET_TAGS', tags);
        // Update other places where copies of these Tags may reside.
        context.commit('UPDATE_CURRENT_USER_PROFILE_TAGS', tags);
        context.commit('UPDATE_SITES_DECODER_TAGS', tags);
      });
    },
    LIST_TAGGABLE_DECODERS(context) {
      return API.listTaggableDecoders().then((response) => {
        const taggableDecoders = response.data;
        context.commit('SET_TAGGABLE_DECODERS', taggableDecoders);
      });
    },
    LIST_SPECIAL_TVFORMATS_ALLOWED_TO_UPLOAD(context) {
      return API.listSpecialTVFormatsAllowedToUpload().then((response) => {
        context.commit(
          'SET_SPECIAL_TVFORMATS_ALLOWED_TO_UPLOAD',
          response.data
        );
      });
    },
    LIST_USER_TVFORMATS_CHOOSABLE(context) {
      return API.listUserTVFormatsChoosable().then((response) => {
        context.commit('SET_USER_TVFORMATS_CHOOSABLE', response.data);
      });
    },
    LIST_USER_TVFORMATS_AVAILABLE(context) {
      return API.listUserTVFormatsAvailable().then((response) => {
        context.commit('SET_USER_TVFORMATS_AVAILABLE', response.data);
      });
    },
    LIST_MANAGED_USERS(context) {
      return API.listManagedUsers().then((response) => {
        context.commit('SET_MANAGED_USERS', response.data);
      });
    },
    LIST_PENDING_USER_INVITATIONS(context) {
      return API.listPendingUserInvitations().then((response) => {
        context.commit('SET_PENDING_USER_INVITATIONS', response.data);
      });
    },
  },
};

const store = new Vuex.Store({
  // https://vuex.vuejs.org/en/strict.html
  strict: process.env.NODE_ENV !== 'production',
  modules: {
    common: commonModule,
  },
});

/**
 * Convenience method to receive the current authtoken from cookies.
 *
 * Can't use a getter for this, as it does not reflect updates correctly.
 */
store.getAuthToken = function () {
  try {
    return API.getCookie('authtoken');
  } catch (error) {
    // The Token might have been changed so that JSON parsing failed.
    // Return null to signal that we basically have no Token at all.
    store.commit('UNSET_CURRENT_USER');
    return null;
  }
};

/**
 * Convenience method to receive the current language from localStorage.
 *
 * This relies on localStorage being populated on app startup, either
 * using a default valuze, a previously user-modified value or the
 * associated URL query param.
 *
 * Can't use a getter for this, as it does not reflect updates correctly.
 */
store.getCurrentLocale = function () {
  return localStorage.getItem('locale');
};

export default store;



// WEBPACK FOOTER //
// ./src/store.js